/**
 * The long-form page the threat (i) buttons point at.
 *
 * Three sections, shortest first, and the equations live in `<details>` blocks
 * rather than in the paragraphs: the reader who wants the idea should be able
 * to get it in a screen, and the reader who wants the arithmetic should not
 * have to go anywhere else for it.
 *
 * **Every paragraph is the selected branch's.** The split is by subject rather
 * than by convenience: the spawn side — group budgets, the boss threshold, the
 * refund factors — is `ai.lua`, which no branch here touches, so it is stated
 * once and is true in all three. What each branch *does* move is the income
 * filling the pool and the price list it is spent against, and everything that
 * depends on those is written per branch rather than quoted from live and left
 * to be wrong on the other two.
 */

import { useEffect } from 'react';

import {
  DEFAULTS,
  effectivePassivePower,
  formatCompact,
  spawnerValue,
  type Ruleset,
  type ScenarioParams,
} from '../../model';
import { useLab } from '../LabContext';
import { useRouter } from '../router';
import { WaveIllustrator } from './WaveIllustrator';

const SECTIONS = [
  { id: 'what', label: 'What is threat' },
  { id: 'dynamics', label: 'Threat dynamics' },
  { id: 'illustrator', label: 'Threat illustrator' },
];

function MathBlock({ children }: { children: React.ReactNode }) {
  return (
    <details className="math">
      <summary>The math</summary>
      <div className="math-body">{children}</div>
    </details>
  );
}

/** The price list, in whichever branch's units are selected. */
function PriceTable({ ruleset }: { ruleset: Ruleset }) {
  const tiers = ['small', 'medium', 'big', 'behemoth'] as const;
  return (
    <table className="doc-table">
      <thead>
        <tr>
          <th>unit</th>
          <th>biter</th>
          <th>spitter</th>
        </tr>
      </thead>
      <tbody>
        {tiers.map((tier) => (
          <tr key={tier}>
            <td>{tier}</td>
            <td>{formatCompact(ruleset.threatValues[`${tier}-biter`], 2)}</td>
            <td>{formatCompact(ruleset.threatValues[`${tier}-spitter`], 2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** What one point of threat *is* on this branch — the scale, in one sentence. */
function ThreatUnit({ ruleset }: { ruleset: Ruleset }) {
  const small = ruleset.threatValues['small-biter'];
  const behemoth = ruleset.threatValues['behemoth-biter'];
  if (ruleset.id === 'live') {
    return (
      <p>
        The scale is arbitrary — a small biter is {formatCompact(small, 1)} and nothing anchors it
        to 1 — so a threat figure only means something next to another threat figure.
      </p>
    );
  }
  return (
    <p>
      Prices here are written as <code>n / small</code> with <code>small = 15</code>, which anchors
      the scale: <b>one point of threat is one small biter</b>, and a behemoth at{' '}
      {formatCompact(behemoth, 0)} says outright how many smalls it is worth. Live's numbers are
      not comparable to these at face value.
      {ruleset.id === 'scenario'
        ? ' The scenario keeps that price list unchanged — it is tuning what fills the pool, not what the pool buys.'
        : ''}
    </p>
  );
}

function WhatIsThreat() {
  const { ruleset } = useLab();
  const behemoth = ruleset.threatValues['behemoth-biter'];
  const perTenK = Math.round(10000 / behemoth);

  return (
    <section id="what" className="doc-section">
      <h3>1 · What is threat</h3>
      <p className="doc-lede">
        Threat is, near enough, <b>the biters your opponent gets</b>. The number in the menu is
        priced in units: every biter has a threat value, and the pool is what the game draws them
        from. A behemoth costs {formatCompact(behemoth, 1)}, so 10,000 threat is about {perTenK} of
        them.
      </p>
      <ThreatUnit ruleset={ruleset} />
      <p>Two things stop it being a straight conversion.</p>
      <p>
        <b>Only part of the pool is sent in a wave.</b> Each attacking group is budgeted a
        twentieth of it, so a four-group attack puts about a fifth of the pool on the map, not all
        of it.
      </p>
      <p>
        <b>The pool is not debited when the wave spawns.</b> It comes off later, and only as those
        biters die. Until then the same threat is still sitting there, and the next wave is sized
        from a pool that still contains it.
      </p>

      <MathBlock>
        <PriceTable ruleset={ruleset} />
        <p>
          {ruleset.threatValues['big-biter'] === ruleset.threatValues['big-spitter'] ? (
            <>
              The two columns are identical here, so which one a group rolls is flavour: a mixed
              group is 1 spitter in 3, and it costs the pool exactly the same either way.
            </>
          ) : (
            <>
              The two columns are <b>not</b> the same, so which one a group rolls is real money — a
              big biter is {formatCompact(ruleset.threatValues['big-biter'], 2)} against a big
              spitter's {formatCompact(ruleset.threatValues['big-spitter'], 2)}. A mixed group is 1
              spitter in 3, so a spitter-heavy roll buys a visibly larger wave for the same threat.
              The illustrator below can split the two apart.
            </>
          )}
        </p>
        <p>Per attacking group, from a pool of Θ:</p>
        <p className="formula">
          budget = Θ/10 · unit cap = min(⌊0.25Θ⌋ + U&#123;6,12&#125;, G)
        </p>
        <p>
          Once boss units are active — that is, once max group size <code>G</code> drops from its
          initial {DEFAULTS.maxGroupSizeInitial} at 70% evolution — that budget splits into{' '}
          <code>Θ/20</code> for normal units and <code>Θ/20</code> for bosses.
        </p>
        <p>Groups per attack is a share of the two teams' combined threat:</p>
        <p className="formula">groups = ⌈7 × Θ_own / (Θ_north + Θ_south)⌉ ∈ [1, 7]</p>
        <p>And the debit, when a unit dies:</p>
        <p className="formula">Θ −= value × φ</p>
        <table className="doc-table">
          <thead>
            <tr>
              <th>φ applies to</th>
              <th>factor</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>boss unit</td>
              <td>26 × H</td>
            </tr>
            <tr>
              <td>spawner</td>
              <td>1 + 9 × E²·²⁵, on clamped evolution</td>
            </tr>
            <tr>
              <td>unit not spawned from positive threat</td>
              <td>0.5</td>
            </tr>
            <tr>
              <td>anything else</td>
              <td>1</td>
            </tr>
          </tbody>
        </table>
        <p className="doc-dim">
          <code>ai.lua</code> — <code>select_units_around_spawner</code> and{' '}
          <code>subtract_threat</code>.
        </p>
      </MathBlock>
    </section>
  );
}

/**
 * Which end of the loop evolution is wired to. The pool has two: the income
 * filling it, and the price list it is spent against. Every branch moves the
 * second one the same way — the raffle is `biter_raffle.lua` and untouched —
 * and they disagree entirely about the first.
 */
function WhatEvolutionMoves({ ruleset }: { ruleset: Ruleset }) {
  if (ruleset.id === 'testing3v3') {
    return (
      <p>
        <b>Evolution moves only one end.</b> Income here is set by the mutagen ever fed, not by
        evolution, so a flask is worth the same income at 300% as at 30%. What evolution still
        changes is what the pool <i>buys</i> — the same threat is fewer, harder biters as the
        raffle shifts up — so climbing evolution makes a fixed income hit harder without earning
        any more of it.
      </p>
    );
  }
  if (ruleset.id === 'scenario') {
    return (
      <p>
        <b>Evolution moves both ends, and one of them faster than linearly.</b> Income is{' '}
        <code>a·E^n + b·E</code> with <code>n</code> above 1, so doubling evolution more than
        doubles the income filling the pool; and the raffle shifts up underneath it, so the same
        threat is fewer, harder biters. That compounding is what the powered term is for, and{' '}
        <code>b·E</code> is the floor under it early, before <code>E^n</code> amounts to anything.
      </p>
    );
  }
  return (
    <p>
      <b>Evolution moves both ends.</b> It raises the income filling the pool — income is{' '}
      <code>25 × E</code>, so a point of evolution is flatly a point of income — and it changes
      what the pool buys, since the same threat is fewer, harder biters as the raffle shifts up.
    </p>
  );
}

/** `bb_threat_income`, as this branch computes it. */
function IncomeFormula({ ruleset, scenario }: { ruleset: Ruleset; scenario: ScenarioParams }) {
  if (ruleset.id === 'testing3v3') {
    return <p className="formula">bb_threat_income = 1.5 × M</p>;
  }
  if (ruleset.id === 'scenario') {
    return (
      <p className="formula">
        bb_threat_income = {fmt(scenario.passiveScale)}·E^
        {fmt(effectivePassivePower(scenario), 3)} + {fmt(scenario.passiveLinear)}·E
      </p>
    );
  }
  return (
    <p className="formula">
      bb_threat_income = {DEFAULTS.threatIncomePerEvoPerSecond} × E
    </p>
  );
}

/** What a send puts into the pool on top of the income, if anything. */
function InstantThreatNote({ ruleset, scenario }: { ruleset: Ruleset; scenario: ScenarioParams }) {
  if (ruleset.id === 'testing3v3') {
    return (
      <p>
        <code>{ruleset.branch}</code> has no instant threat, so passive income is the pool's only
        source.
      </p>
    );
  }
  if (ruleset.id === 'scenario') {
    return (
      <p>
        A send adds threat on top of all this, immediately, at a flat{' '}
        <code>T = {fmt(scenario.instantScale)}·M</code>. Being flat is the point: it does not bend
        with evolution the way live's does, so what a send is worth relative to the income it buys
        is decided entirely by the passive curve above it. The player count does not enter — the
        crowd modifier is gone.
      </p>
    );
  }
  return (
    <p>
      A send adds threat on top of all this, immediately. Its rate depends only on where evolution
      currently is, so a large send walks itself down its own curve — concave below 100%
      evolution, convex above it, where the <code>1 + 3(E−1)</code> term takes over.
    </p>
  );
}

/** Trim to the shortest form that still reads exactly. */
function fmt(value: number, digits = 3): string {
  if (!Number.isFinite(value)) return '—';
  return Number(value.toFixed(digits)).toString();
}

function ThreatDynamics() {
  const { ruleset, scenario } = useLab();
  const spawnerAt100 = spawnerValue(ruleset, 1);
  const behemothValue = ruleset.threatValues['behemoth-biter'];

  return (
    <section id="dynamics" className="doc-section">
      <h3>2 · Threat dynamics</h3>
      <p className="doc-lede">
        Because a wave is a fraction of the pool, the pool feeds back on itself — and that is what
        makes threat settle at a level rather than pile up.
      </p>
      <p>
        <b>A higher pool is spent faster.</b> Twice the threat sends twice the biters, so twice as
        much comes back off when they die. It levels off where one wave returns what one attack
        cycle earned.
      </p>
      <p>
        <b>Not killing a wave makes the next one bigger.</b> Nothing is debited for a biter that is
        still alive, so the pool stays where it was and the following attack is drawn from it
        again. Leaks compound into the wave after them.
      </p>
      <p>
        <b>Clearing nests spends threat without waiting for a wave.</b> A spawner is worth{' '}
        {formatCompact(spawnerAt100, 0)} threat at 100% evolution, and killing it takes that off
        the pool directly.
      </p>
      <WhatEvolutionMoves ruleset={ruleset} />

      <p>
        <b>Bosses price in their health; normal biters do not.</b> A normal unit is charged its
        flat price from the table above — {formatCompact(behemothValue, 1)} for a behemoth, at any
        evolution. A boss of the same tier is charged that same price{' '}
        <code>× 20 × H</code>. But both carry the <code>H</code> health factor, so once{' '}
        <code>H</code> leaves 1 a normal biter is <code>H</code> times better value per point of
        health than a boss, and the budget stops buying bosses. The wave turns from a few bosses
        into a crowd.
      </p>

      <MathBlock>
        <p>Income, added every second and summed over the two minutes between attacks:</p>
        <IncomeFormula ruleset={ruleset} scenario={scenario} />
        <p>The level it settles at is where the two sides of the loop meet:</p>
        <p className="formula">refund per cycle = income per cycle</p>
        <p>
          Below 70% evolution there are no bosses, so each of four groups returns <code>Θ/10</code>{' '}
          and the balance is exact:
        </p>
        <p className="formula">0.4 × Θ = income → Θ = 2.5 × income</p>
        <p>
          Above it the normal budget halves, which would push the pool higher — but a boss costs{' '}
          <code>value × 20H</code> from the budget and refunds <code>value × 26H</code> when it
          dies, so it drains more than it was allotted and pulls the level back. The two do not
          cancel cleanly, and a boss is affordable or it is not, so from there on the level has to
          be found by running the loop rather than solved. That is what the charts below do.
        </p>
        <p>
          Bosses take the other half of the budget, so with a mean unit value of <code>v̄</code>{' '}
          each group affords about
        </p>
        <p className="formula">bosses ≈ (Θ/20) / (v̄ × 20 × H)</p>
        <p>
          which is why they are at their most numerous the moment they appear at 70% evolution and
          thin out from there. <code>v̄</code> more than doubles as behemoths enter the raffle at
          90%, then <code>H</code> runs from 1 to 10 by 325% — both faster than Θ grows. The
          attempt cap of <code>⌈(300 − G)/20⌉</code> rises over the same range and never becomes
          the binding constraint.
        </p>
        <InstantThreatNote ruleset={ruleset} scenario={scenario} />
      </MathBlock>
    </section>
  );
}

function Illustrator() {
  const { ruleset } = useLab();
  return (
    <section id="illustrator" className="doc-section">
      <h3>3 · Threat illustrator</h3>
      <p className="doc-lede">
        Where the level lands at each evolution, and the wave it draws — solved by running the
        ported spawn loop until income and refund agree, against{' '}
        <code>{ruleset.branch}</code>'s income and price list.
      </p>
      <p>
        The evolution axis is the app's shared zoom, so the window follows you here from the charts
        and back.
        {ruleset.id === 'scenario'
          ? ' Because the whole chart hangs off the passive curve, its three constants are on the page: move one and the level moves with it.'
          : ''}
      </p>
      <WaveIllustrator />
    </section>
  );
}

export function UnderstandingThreat() {
  const { section } = useRouter();

  // An (i) that says "→ Dynamics" has to land on the paragraph, not the top of
  // the page.
  useEffect(() => {
    if (!section) {
      window.scrollTo({ top: 0 });
      return;
    }
    const target = document.getElementById(section);
    if (target) target.scrollIntoView({ block: 'start' });
  }, [section]);

  return (
    <div className="doc">
      <nav className="doc-toc" aria-label="Sections">
        <span className="doc-toc-title">Understanding threat</span>
        {SECTIONS.map((entry) => (
          <a key={entry.id} href={`#${entry.id}`} className="doc-toc-link">
            {entry.label}
          </a>
        ))}
      </nav>
      <div className="doc-body">
        <WhatIsThreat />
        <ThreatDynamics />
        <Illustrator />
      </div>
    </div>
  );
}
